import { useState, useEffect, useRef } from "react";
import {
  Search,
  Loader2,
  ExternalLink,
  ArrowRight,
  Flame,
  ShoppingCart,
  Star,
  TrendingUp,
  GraduationCap,
  Image,
  Layers,
  Upload,
  X,
  Camera,
  Clock,
  Filter,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import axios from "axios";
import { toast } from "sonner";
import WatchlistButton from "../components/WatchlistButton";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  { code: "fr", flag: "\u{1F1EB}\u{1F1F7}", label: "France", currency: "\u20AC" },
  { code: "us", flag: "\u{1F1FA}\u{1F1F8}", label: "USA", currency: "$" },
  { code: "de", flag: "\u{1F1E9}\u{1F1EA}", label: "Allemagne", currency: "\u20AC" },
  { code: "gb", flag: "\u{1F1EC}\u{1F1E7}", label: "UK", currency: "\u00A3" },
  { code: "cn", flag: "\u{1F1E8}\u{1F1F3}", label: "Chine", currency: "HK$" },
];

const SELLING_MARKETPLACES = [
  "eBay", "Amazon", "Cdiscount", "Aliexpress", "Leboncoin", "Vinted",
  "Etsy", "Rue du Commerce", "Fnac", "Rakuten", "Facebook Marketplace",
  "Redbubble", "Temu", "TikTok Shop", "VidaXL", "BackMarket",
];

const isSellingMarketplace = (name) =>
  SELLING_MARKETPLACES.some(m => name?.toLowerCase().includes(m.toLowerCase()));

const TABS = [
  { id: "keyword", icon: Search, label: "Mots-clés" },
  { id: "niche", icon: Layers, label: "Niche" },
  { id: "image", icon: Image, label: "Image" },
];

const SUGGESTIONS = [
  "iPhone 15 Pro", "Nike Air Jordan 1", "PS5 Slim", "AirPods Pro 2",
  "MacBook Air M3", "Dyson V15", "Samsung Galaxy S24", "Nintendo Switch OLED",
];

// ========================
// SCAN HISTORY COMPONENT
// ========================

function ScanHistory({ history, onReplay }) {
  if (!history || history.length === 0) return null;

  const typeLabels = { keyword: "Mots-clés", niche: "Niche", image: "Image" };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `il y a ${hrs}h`;
    return `il y a ${Math.floor(hrs / 24)}j`;
  };

  return (
    <div className="neo-card p-4" data-testid="scan-history">
      <h3 className="font-bold uppercase tracking-wider text-xs flex items-center gap-2 mb-3">
        <Clock size={14} className="text-[#00E676]" />
        Scans récents
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {history.map((h, idx) => (
          <button
            key={idx}
            onClick={() => onReplay(h)}
            className="border-2 border-[#0A0A0A] dark:border-[#00E676]/30 p-3 text-left hover:bg-[#00E676]/5 hover:border-[#00E676] transition-all group"
            data-testid={`history-item-${idx}`}
          >
            <div className="flex items-start gap-2.5">
              {h.product_image ? (
                <img src={h.product_image} alt="" className="w-10 h-10 object-contain border border-[#0A0A0A]/20 flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
                  <Search size={14} className="text-[#00E676]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate group-hover:text-[#00E676] transition-colors">{h.query}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30">
                    {typeLabels[h.scan_type] || h.scan_type}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{timeAgo(h.scanned_at)}</span>
                </div>
                {h.opportunities_count > 0 && (
                  <p className="text-xs text-[#00E676] font-bold mt-1">
                    {h.opportunities_count} opp. {h.best_margin_percent ? `(+${h.best_margin_percent}%)` : ""}
                  </p>
                )}
                {h.best_price > 0 && h.opportunities_count === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{h.best_price.toFixed(2)}{"\u20AC"}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ========================
// SCAN RESULTS COMPONENT
// ========================

function ScanResults({ result, query, minMargin = 5, country = "fr" }) {
  if (!result) return null;

  const sym = "\u20AC"; // All prices are converted to EUR by the backend
  const filteredOpps = result.opportunities?.filter(o => o.margin_percent >= minMargin) || [];

  return (
    <>
      {result.identified_product && (
        <div className="bg-blue-500/10 border-2 border-blue-500 p-3 flex items-center gap-2 text-sm">
          <Camera size={16} className="text-blue-500" />
          <span>Produit identifié par IA : <strong>{result.identified_product}</strong></span>
        </div>
      )}

      {result.product_name && result.current_price > 0 && (
        <div className="neo-card p-4 sm:p-6 border-[#00E676]" data-testid="scan-result-header">
          <div className="flex gap-4 items-start">
            {result.product_image && (
              <img src={result.product_image} alt="" className="w-20 h-20 sm:w-24 sm:h-24 object-contain border-2 border-[#0A0A0A] flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-base sm:text-lg truncate">{result.product_name}</p>
              <p className="text-sm text-muted-foreground">{result.platform}</p>
              <p className="text-2xl font-extrabold text-[#00E676] mt-1">{(result.current_price || 0).toFixed(2)}{sym}</p>
              <p className="text-xs text-muted-foreground">Meilleur prix trouve</p>
              <div className="flex items-center gap-2 mt-3">
                {result.products?.[0]?.url && (
                  <a href={result.products[0].url} target="_blank" rel="noopener noreferrer" className="neo-button-primary text-xs px-4 py-2 flex items-center gap-1.5" data-testid="view-product-btn">
                    <ExternalLink size={14} /> Voir le produit
                  </a>
                )}
                <WatchlistButton product={{ product_name: result.product_name, search_query: query, product_image: result.product_image, buy_platform: result.platform, buy_price: result.current_price, buy_url: result.products?.[0]?.url }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredOpps.length > 0 && (
        <div className="neo-card p-4 sm:p-6" data-testid="scan-opportunities">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-4 sm:mb-6 flex items-center gap-2">
            <Flame className="text-[#00E676]" size={22} />
            Opportunités ({filteredOpps.length})
          </h2>
          <div className="space-y-4">
            {filteredOpps.map((opp, idx) => (
              <div key={idx} className="border-2 border-[#0A0A0A] dark:border-[#00E676] p-3 sm:p-4 relative" data-testid={`scan-opp-${idx}`}>
                <div className="absolute top-2 right-2 bg-[#00E676] text-[#0A0A0A] border-2 border-[#0A0A0A] px-2 sm:px-3 py-1 font-bold text-sm transform rotate-2">
                  +{opp.margin_percent}%
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pr-16 sm:pr-20">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground font-bold">Acheter sur</p>
                    <p className="font-semibold text-sm">{opp.buy_platform}</p>
                    <p className="text-xl font-bold">{(opp.buy_price || 0).toFixed(2)}{sym}</p>
                    {opp.buy_url && (
                      <a href={opp.buy_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00E676] inline-flex items-center gap-1 mt-1">
                        Voir <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center justify-center"><ArrowRight className="text-[#00E676] hidden sm:block" size={28} /></div>
                  <div className={isSellingMarketplace(opp.sell_platform) ? "bg-[#00E676]/10 border-2 border-[#00E676] p-3 -m-1" : ""}>
                    <p className="text-xs uppercase text-muted-foreground font-bold">Vendre sur</p>
                    <p className={`font-semibold text-sm ${isSellingMarketplace(opp.sell_platform) ? 'text-[#00E676]' : ''}`}>
                      {opp.sell_platform}
                      {isSellingMarketplace(opp.sell_platform) && <span className="ml-1 text-[10px] bg-[#00E676] text-[#0A0A0A] px-1.5 py-0.5 font-bold align-middle">TOP</span>}
                    </p>
                    <p className="text-xl font-bold text-[#00E676]">{(opp.sell_price || 0).toFixed(2)}{sym}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {opp.sell_url && <a href={opp.sell_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00E676] inline-flex items-center gap-1">Voir <ExternalLink size={12} /></a>}
                      {isSellingMarketplace(opp.sell_platform) && <a href="https://www.profimy-academie.com/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#FFCC00] inline-flex items-center gap-1 hover:underline"><GraduationCap size={10} /> Formation</a>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 bg-[#0A0A0A] text-white p-3 flex justify-between items-center">
                  <span className="font-bold text-xs sm:text-sm uppercase">Profit estimé</span>
                  <div className="flex items-center gap-3">
                    <WatchlistButton product={{ product_name: result.product_name, search_query: query, product_image: result.product_image, buy_platform: opp.buy_platform, buy_price: opp.buy_price, sell_platform: opp.sell_platform, sell_price: opp.sell_price, margin: opp.margin, margin_percent: opp.margin_percent, buy_url: opp.buy_url, sell_url: opp.sell_url }} />
                    <span className="text-[#00E676] font-bold text-lg">+{(opp.margin || 0).toFixed(2)}{sym}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.products?.length > 0 && (
        <div className="neo-card p-4 sm:p-6" data-testid="scan-products">
          <h2 className="text-base md:text-lg font-bold uppercase mb-4 sm:mb-6 flex items-center gap-2">
            <ShoppingCart size={20} /> Tous les vendeurs ({result.products.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {result.products.map((product, idx) => (
              <div key={idx} className="border-2 border-[#0A0A0A] dark:border-[#00E676]/50 p-3 sm:p-4 hover:bg-[#00E676]/5 transition-colors" data-testid={`product-${idx}`}>
                <div className="flex gap-3">
                  {product.image && <img src={product.image} alt="" className="w-14 h-14 sm:w-16 sm:h-16 object-contain border border-[#0A0A0A]/20 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-bold text-sm line-clamp-2 flex-1">{product.title}</p>
                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase border flex-shrink-0 ${product.condition === "NEW" ? "border-[#00E676]/50 text-[#00E676]" : product.condition === "REFURBISHED" ? "border-[#FFCC00]/50 text-[#FFCC00]" : "border-[#FF6B35]/50 text-[#FF6B35]"}`}>
                        {product.condition === "NEW" ? "Neuf" : product.condition === "REFURBISHED" ? "Reconditionne" : "Occasion"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{product.seller}</p>
                    {product.rating && <div className="flex items-center gap-1 mt-0.5"><Star size={10} className="text-yellow-500 fill-yellow-500" /><span className="text-xs">{product.rating}</span></div>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#0A0A0A]/20">
                  <div>
                    <span className="text-lg font-bold">{(product.price || 0).toFixed(2)}{sym}</span>
                    {product.original_price && <span className="text-sm text-muted-foreground line-through ml-2">{(product.original_price || 0).toFixed(2)}{sym}</span>}
                  </div>
                  <div className="flex gap-2">
                    <WatchlistButton product={{ product_name: product.title, search_query: query, product_image: product.image, buy_platform: product.seller, buy_price: product.price, buy_url: product.url }} />
                    {product.url && <a href={product.url} target="_blank" rel="noopener noreferrer" className="neo-button-secondary text-xs px-3 py-1 flex items-center gap-1"><ExternalLink size={12} /> Voir</a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-right">Source : {result.source || "Google Shopping"}</p>
        </div>
      )}

      {!result.products?.length && !result.opportunities?.length && result.error && (
        <div className="neo-card p-8 sm:p-12 text-center">
          <Search className="mx-auto mb-4 text-muted-foreground" size={48} />
          <p className="text-lg font-bold">{result.error}</p>
          <p className="text-muted-foreground mt-2">Essayez un autre terme de recherche</p>
        </div>
      )}
    </>
  );
}

// ========================
// MAIN SCANNER
// ========================

export default function Scanner() {
  const { email: userEmail } = useAuth();
  const [activeTab, setActiveTab] = useState("keyword");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Niche state
  const [niches, setNiches] = useState([]);
  const [selectedNiche, setSelectedNiche] = useState(null);
  const [nicheQueries, setNicheQueries] = useState([]);
  const [loadingNiches, setLoadingNiches] = useState(false);

  // Image state
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const fileInputRef = useRef(null);

  // Country state
  const [country, setCountry] = useState("fr");

  // Margin filter state
  const [minMargin, setMinMargin] = useState(5);

  // History state
  const [history, setHistory] = useState([]);

  // Load niches + history on mount
  useEffect(() => {
    axios.get(`${API}/scanner/niches`).then(r => setNiches(r.data.niches)).catch(() => {});
    loadHistory();
  }, []);

  const loadHistory = () => {
    const emailParam = userEmail ? `&email=${encodeURIComponent(userEmail)}` : "";
    axios.get(`${API}/scanner/history?limit=3${emailParam}`).then(r => setHistory(r.data.history)).catch(() => {});
  };

  const saveToHistory = (scanQuery, scanType, data) => {
    const bestOpp = data.opportunities?.[0];
    const entry = {
      query: scanQuery,
      scan_type: scanType,
      product_name: data.product_name || null,
      product_image: data.product_image || null,
      best_price: data.current_price || null,
      opportunities_count: data.opportunities?.length || 0,
      best_margin_percent: bestOpp?.margin_percent || null,
      user_email: userEmail || "",
    };
    axios.post(`${API}/scanner/history`, entry).then(() => loadHistory()).catch(() => {});
  };

  // ---- KEYWORD SCAN ----
  const handleKeywordScan = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true); setResult(null);
    try {
      const response = await axios.post(`${API}/scan`, { url: query.trim(), country });
      setResult(response.data);
      saveToHistory(query.trim(), "keyword", response.data);
      if (response.data.opportunities?.length > 0) toast.success(`${response.data.opportunities.length} opportunité(s) trouvée(s) !`);
      else if (response.data.products?.length > 0) toast.info("Produits trouvés, pas d'écart de prix significatif.");
      else toast.info("Aucun résultat.");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur lors du scan"); }
    finally { setLoading(false); }
  };

  const quickSearch = (q) => { setQuery(q); setActiveTab("keyword"); setTimeout(() => document.getElementById("scan-form")?.requestSubmit(), 100); };

  // ---- NICHE SCAN ----
  const selectNiche = async (nicheId) => {
    setSelectedNiche(nicheId); setLoadingNiches(true);
    try {
      const r = await axios.get(`${API}/scanner/niche/${nicheId}`);
      setNicheQueries(r.data.queries);
    } catch { toast.error("Erreur"); }
    finally { setLoadingNiches(false); }
  };

  // ---- IMAGE SCAN ----
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      const base64 = ev.target.result.split(",")[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleImageScan = async () => {
    if (!imageBase64) return;
    setLoading(true); setResult(null);
    try {
      const response = await axios.post(`${API}/scanner/image`, { image_base64: imageBase64, mime_type: "image/jpeg", country });
      setResult(response.data);
      if (response.data.identified_product) {
        setQuery(response.data.identified_product);
        saveToHistory(response.data.identified_product, "image", response.data);
        if (response.data.opportunities?.length > 0) toast.success(`${response.data.opportunities.length} opportunité(s) !`);
        else toast.info(`Produit identifié : ${response.data.identified_product}`);
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur d'analyse d'image"); }
    finally { setLoading(false); }
  };

  const replayHistory = (h) => {
    quickSearch(h.query);
  };

  const switchTab = (tabId) => { setActiveTab(tabId); setResult(null); };

  return (
    <div className="space-y-6" data-testid="scanner-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Search className="text-[#00E676]" size={36} />
          SCANNER
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-xs sm:text-sm">
          3 méthodes pour trouver des opportunités d'arbitrage
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2" data-testid="scanner-tabs">
        {TABS.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => switchTab(id)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 font-bold text-xs sm:text-sm uppercase tracking-wider border-2 whitespace-nowrap transition-all ${
              activeTab === id
                ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A]"
                : "border-[#0A0A0A] dark:border-[#00E676]/30 hover:bg-[#00E676]/10"
            }`}
            data-testid={`tab-${id}`}
          >
            <Icon size={16} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Country Selector */}
      <div className="flex items-center gap-2" data-testid="country-selector">
        <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Marché :</span>
        <div className="flex gap-1">
          {COUNTRIES.map(({ code, flag, label }) => (
            <button
              key={code}
              onClick={() => setCountry(code)}
              className={`flex items-center gap-1.5 px-3 py-1.5 border-2 text-sm font-bold transition-all ${
                country === code
                  ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A] shadow-[2px_2px_0px_#0A0A0A]"
                  : "border-[#0A0A0A] dark:border-[#00E676]/30 hover:bg-[#00E676]/10"
              }`}
              data-testid={`country-${code}`}
            >
              <span className="text-lg">{flag}</span>
              <span className="hidden sm:inline text-xs">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Margin Filter */}
      <div className="neo-card p-3 flex items-center gap-4" data-testid="margin-filter">
        <Filter size={16} className="text-muted-foreground flex-shrink-0" />
        <span className="text-xs font-bold whitespace-nowrap">Marge min: {minMargin}%</span>
        <Slider
          value={[minMargin]}
          onValueChange={(val) => setMinMargin(val[0])}
          min={0}
          max={50}
          step={5}
          className="flex-1 max-w-xs"
        />
      </div>

      {/* Scan History - visible on all tabs */}
      {!result && !loading && <ScanHistory history={history} onReplay={replayHistory} />}

      {/* ==================== KEYWORD TAB ==================== */}
      {activeTab === "keyword" && (
        <>
          <form id="scan-form" onSubmit={handleKeywordScan} className="neo-card p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 relative">
                <Input type="text" placeholder="Nom du produit (ex: iPhone 15 Pro, Nike Air Jordan 1...)" value={query} onChange={(e) => setQuery(e.target.value)} className="neo-input w-full pr-12" data-testid="scanner-input" />
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              </div>
              <button type="submit" disabled={loading || !query.trim()} className="neo-button-primary inline-flex items-center justify-center gap-2 min-w-[140px] sm:min-w-[180px]" data-testid="scanner-submit">
                {loading ? <><Loader2 size={20} className="animate-spin" /> Analyse...</> : <><Search size={20} /> Scanner</>}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Données en temps réel de Google Shopping</p>
          </form>
          {!result && !loading && (
            <div className="flex flex-wrap gap-2" data-testid="suggestions">
              {SUGGESTIONS.map((s) => (<button key={s} onClick={() => quickSearch(s)} className="neo-button-secondary text-xs sm:text-sm px-3 py-2">{s}</button>))}
            </div>
          )}
        </>
      )}

      {/* ==================== NICHE TAB ==================== */}
      {activeTab === "niche" && (
        <div className="space-y-4" data-testid="niche-panel">
          {!selectedNiche ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="niche-grid">
              {niches.map((niche) => (
                <button
                  key={niche.id}
                  onClick={() => selectNiche(niche.id)}
                  className="neo-card p-4 text-left hover:border-[#00E676] transition-colors group"
                  data-testid={`niche-${niche.id}`}
                >
                  <h3 className="font-bold text-sm sm:text-base mb-1 group-hover:text-[#00E676]">{niche.label}</h3>
                  <p className="text-xs text-muted-foreground">{niche.subcategories.length} sous-catégories</p>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <button onClick={() => { setSelectedNiche(null); setNicheQueries([]); }} className="neo-button-secondary text-xs px-3 py-1.5">
                  <X size={14} />
                </button>
                <h3 className="font-bold uppercase tracking-wider text-sm">{niches.find(n => n.id === selectedNiche)?.label}</h3>
              </div>
              {loadingNiches ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#00E676]" size={32} /></div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {nicheQueries.map((q, idx) => (
                    <button key={idx} onClick={() => quickSearch(q)} className="neo-button-secondary text-xs sm:text-sm px-3 py-2 hover:bg-[#00E676]/20">
                      <Search size={12} className="mr-1.5 inline" />{q}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== IMAGE TAB ==================== */}
      {activeTab === "image" && (
        <div className="neo-card p-4 sm:p-6 space-y-4" data-testid="image-panel">
          <h3 className="font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Camera size={18} className="text-[#00E676]" />
            Scanner par image (GPT-4o)
          </h3>
          <p className="text-xs text-muted-foreground">Uploadez une photo du produit et l'IA identifiera automatiquement le produit pour trouver les meilleurs prix.</p>

          {!imagePreview ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-[#0A0A0A] dark:border-[#00E676]/40 p-8 sm:p-12 text-center cursor-pointer hover:bg-[#00E676]/5 transition-colors"
              data-testid="image-dropzone"
            >
              <Upload className="mx-auto mb-3 text-muted-foreground" size={40} />
              <p className="font-bold text-sm">Cliquez pour uploader une image</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG ou WEBP</p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <img src={imagePreview} alt="Preview" className="w-full sm:w-48 h-48 object-contain border-2 border-[#0A0A0A]" />
              <div className="flex-1 space-y-3">
                <p className="text-sm font-bold text-[#00E676]">Image chargée</p>
                <div className="flex gap-2">
                  <button onClick={handleImageScan} disabled={loading} className="neo-button-primary flex items-center gap-2" data-testid="image-scan-btn">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                    {loading ? "Analyse IA..." : "Identifier & Scanner"}
                  </button>
                  <button onClick={() => { setImagePreview(null); setImageBase64(null); }} className="neo-button-secondary px-3">
                    <X size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} className="hidden" data-testid="image-file-input" />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="neo-card p-6 animate-pulse">
              <div className="flex gap-4"><div className="w-20 h-20 bg-muted" /><div className="flex-1 space-y-3"><div className="h-4 bg-muted w-3/4" /><div className="h-3 bg-muted w-1/2" /><div className="h-5 bg-muted w-1/4" /></div></div>
            </div>
          ))}
        </div>
      )}

      {/* Scan Results */}
      {result && !loading && <ScanResults result={result} query={query} minMargin={minMargin} country={country} />}

      {/* Empty state */}
      {!result && !loading && activeTab === "keyword" && history.length === 0 && (
        <div className="neo-card p-8 sm:p-12 text-center" data-testid="scanner-empty">
          <TrendingUp className="mx-auto mb-4 text-muted-foreground" size={48} />
          <p className="text-lg sm:text-xl font-bold text-muted-foreground">Scannez un produit</p>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto text-sm">Entrez le nom d'un produit ou uploadez une image</p>
        </div>
      )}
    </div>
  );
}
